// CardDetectorScreen.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Slider,
    Stack,
    Typography,
} from "@mui/material";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
    getCardKeyFromText,
    getPictogramsForPackets,
    loadInitialState,
} from "../components/RadicalHelpers";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import * as ort from "onnxruntime-web/webgpu";
import { reportError } from "../supabaseClient";

const MODELS = {
    normal: {
        label: "Normal",
        url: "/models/playingCards.onnx", // yolo export model=playingCards.pt format=onnx quantize=16 nms=True imgsz=416
        channels: 3,
    },
    gray: {
        label: "Escala de grises",
        url: "/models/playingCards_gray.onnx",
        channels: 1,
    },
};
const MODEL_SIZE = 416;

const CLASS_NAMES = [
    "10C",
    "10D",
    "10H",
    "10S",
    "2C",
    "2D",
    "2H",
    "2S",
    "3C",
    "3D",
    "3H",
    "3S",
    "4C",
    "4D",
    "4H",
    "4S",
    "5C",
    "5D",
    "5H",
    "5S",
    "6C",
    "6D",
    "6H",
    "6S",
    "7C",
    "7D",
    "7H",
    "7S",
    "8C",
    "8D",
    "8H",
    "8S",
    "9C",
    "9D",
    "9H",
    "9S",
    "AC",
    "AD",
    "AH",
    "AS",
    "JC",
    "JD",
    "JH",
    "JS",
    "KC",
    "KD",
    "KH",
    "KS",
    "QC",
    "QD",
    "QH",
    "QS",
];

const SUITS = {
    C: {
        symbol: "♣",
        name: "tréboles",
        color: "#1f2937",
    },
    D: {
        symbol: "♦",
        name: "diamantes",
        color: "#d32f2f",
    },
    H: {
        symbol: "♥",
        name: "corazones",
        color: "#d32f2f",
    },
    S: {
        symbol: "♠",
        name: "picas",
        color: "#1f2937",
    },
};

const RANKS = {
    A: "As",
    J: "Jota",
    Q: "Reina",
    K: "Rey",
};

const PICTOGRAM_SPEECH = {
    "⬅": "izquierda",
    "◀": "izquierda",
    "➡": "derecha",
    "▶": "derecha",
    "⬆": "arriba",
    "⬇": "abajo",
    "↖": "arriba izquierda",
    "↗": "arriba derecha",
    "↙": "abajo izquierda",
    "↘": "abajo derecha",
    "⏹": "centro",
};

const LOCK_SCREEN_STYLE = {
    backgroundColor: "#000000",
    textColor: "#ADDB889D",
    fontFamily: '"Roboto", "Arial", sans-serif',

    timeFontSize: "clamp(99px, 28vw, 162px)",
    timeFontWeight: 700,
    timeLetterSpacing: "-0.055em",
    timeLineHeight: 0.9,

    dateFontSize: "clamp(18px, 5vw, 28px)",
    dateFontWeight: 400,

    alarmIconSize: "clamp(24px, 6vw, 34px)",
    cardFontSize: "clamp(12px, 3.5vw, 17px)",
    cardFontWeight: 500,

    contentTop: "14dvh",
    horizontalPadding: "20px",
    alarmMarginTop: "34px",
    alarmGap: "2px",
    zIndex: 2147483647,
};

const LOCK_TIME_FORMAT = new Intl.DateTimeFormat("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});

const LOCK_DATE_FORMAT = new Intl.DateTimeFormat("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "long",
});

const modelSessionPromises = {};

const SHORT_RANKS = {
    A: "01",
    2: "02",
    3: "03",
    4: "04",
    5: "05",
    6: "06",
    7: "07",
    8: "08",
    9: "09",
    10: "10",
    J: "11",
    Q: "12",
    K: "13",
};

const SHORT_SUITS = {
    "♠": "00",
    "♥": "15",
    "♣": "30",
    "♦": "45",
};

const CardDetectorScreen = () => {
    const videoRef = useRef(null);
    const inputCanvasRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const streamRef = useRef(null);
    const sessionRef = useRef(null);
    const inferenceRunningRef = useRef(false);
    const stableDetectionRef = useRef({
        label: "",
        hits: 0,
        misses: 0,
    });

    const [modelType, setModelType] = useState("normal");
    const [modelStatus, setModelStatus] = useState("loading");
    const [provider, setProvider] = useState("");
    const [cameraActive, setCameraActive] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");
    const [confidence, setConfidence] = useState(0.55);
    const [detectedCard, setDetectedCard] = useState(null);
    const [aspectRatio, setAspectRatio] = useState("16 / 9");
    const [loadingCamera, setLoadingCamera] = useState(false);
    const [inferenceMs, setInferenceMs] = useState(null);
    const [error, setError] = useState("");

    const [radicalState] = useState(loadInitialState);
    const radicalPresets = useMemo(
        () =>
            radicalState.userPresets.filter(
                (preset) => preset.recipeType !== "advanced",
            ),
        [radicalState.userPresets],
    );
    const [selectedPresetId, setSelectedPresetId] = useState(() =>
        radicalState.userPresets.some(
            (preset) =>
                preset.recipeType !== "advanced" &&
                preset.id === radicalState.selectedPresetId,
        )
            ? radicalState.selectedPresetId
            : "",
    );
    const [dealIndex, setDealIndex] = useState(0);

    const selectedPreset = useMemo(
        () =>
            radicalPresets.find((preset) => preset.id === selectedPresetId) ||
            null,
        [radicalPresets, selectedPresetId],
    );

    const presetPictograms = useMemo(
        () =>
            selectedPreset
                ? getPictogramsForPackets(
                      selectedPreset.packets,
                      radicalState.pictogramsByPackets,
                  )
                : {},
        [radicalState.pictogramsByPackets, selectedPreset],
    );

    useEffect(() => {
        let cancelled = false;

        setModelStatus("loading");
        setProvider("");
        setError("");
        sessionRef.current = null;

        if (!modelSessionPromises[modelType]) {
            modelSessionPromises[modelType] = (async () => {
                if (navigator.gpu) {
                    try {
                        ort.env.webgpu.powerPreference = "high-performance";

                        return {
                            session: await ort.InferenceSession.create(
                                MODELS[modelType].url,
                                {
                                    executionProviders: ["webgpu", "wasm"],
                                    graphOptimizationLevel: "all",
                                    enableGraphCapture: false,
                                },
                            ),
                            provider: "WebGPU + WASM",
                        };
                    } catch (webGpuError) {
                        void reportError(
                            "card-detector-webgpu",
                            webGpuError,
                            {
                                modelType,
                                modelUrl: MODELS[modelType].url,
                            },
                            "warning",
                        );
                        console.warn(
                            "WebGPU initialization failed. Falling back to WASM.",
                            webGpuError,
                        );
                    }
                }

                return {
                    session: await ort.InferenceSession.create(
                        MODELS[modelType].url,
                        {
                            executionProviders: ["wasm"],
                            graphOptimizationLevel: "all",
                        },
                    ),
                    provider: "WASM",
                };
            })();
        }

        modelSessionPromises[modelType]
            .then(({ session, provider: nextProvider }) => {
                if (cancelled) return;

                sessionRef.current = session;
                setProvider(nextProvider);
                setModelStatus("ready");
            })
            .catch((modelError) => {
                void reportError("card-detector-model", modelError, {
                    modelType,
                    modelUrl: MODELS[modelType].url,
                });
                delete modelSessionPromises[modelType];

                if (cancelled) return;

                setModelStatus("error");
                setError(
                    `No se pudo cargar el modelo ONNX: ${modelError.message}`,
                );
            });

        return () => {
            cancelled = true;
        };
    }, [modelType]);

    const stopCamera = useCallback(() => {
        streamRef.current?.getTracks().forEach((track) => track.stop());

        streamRef.current = null;

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        const overlay = overlayCanvasRef.current;

        overlay
            ?.getContext("2d")
            ?.clearRect(0, 0, overlay.width, overlay.height);

        stableDetectionRef.current = {
            label: "",
            hits: 0,
            misses: 0,
        };

        setCameraActive(false);
        setDetectedCard(null);
        setInferenceMs(null);
    }, []);

    const startCamera = useCallback(
        async (mode = facingMode) => {
            setError("");
            setLoadingCamera(true);
            setCameraActive(false);

            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error(
                        "Este navegador no permite acceder a la cámara.",
                    );
                }

                streamRef.current?.getTracks().forEach((track) => track.stop());

                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: {
                            ideal: mode,
                        },
                        width: {
                            ideal: 640,
                        },
                        height: {
                            ideal: 480,
                        },
                    },
                });

                streamRef.current = stream;
                videoRef.current.srcObject = stream;

                await videoRef.current.play();

                setAspectRatio(
                    `${videoRef.current.videoWidth} / ${videoRef.current.videoHeight}`,
                );

                stableDetectionRef.current = {
                    label: "",
                    hits: 0,
                    misses: 0,
                };

                setDetectedCard(null);
                setCameraActive(true);
            } catch (cameraError) {
                stopCamera();
                void reportError(
                    "card-detector-camera",
                    cameraError,
                    {
                        facingMode: mode,
                        modelType,
                        modelStatus,
                    },
                    cameraError.name === "NotAllowedError"
                        ? "warning"
                        : "error",
                );

                setError(
                    cameraError.name === "NotAllowedError"
                        ? "No se ha concedido permiso para usar la cámara."
                        : cameraError.message,
                );
            } finally {
                setLoadingCamera(false);
            }
        },
        [facingMode, stopCamera],
    );

    useEffect(
        () => () =>
            streamRef.current?.getTracks().forEach((track) => track.stop()),
        [],
    );

    useEffect(() => {
        if (!cameraActive || modelStatus !== "ready") {
            return undefined;
        }

        let cancelled = false;
        let timeout;

        const detect = async () => {
            const video = videoRef.current;

            if (cancelled) return;

            if (inferenceRunningRef.current) {
                timeout = window.setTimeout(detect, 0);
                return;
            }

            if (!video?.videoWidth || video.readyState < 2) {
                timeout = window.setTimeout(detect, 0);
                return;
            }

            inferenceRunningRef.current = true;

            try {
                const totalStartedAt = performance.now();
                const sourceWidth = video.videoWidth;
                const sourceHeight = video.videoHeight;
                const scale = Math.min(
                    MODEL_SIZE / sourceWidth,
                    MODEL_SIZE / sourceHeight,
                );
                const resizedWidth = Math.round(sourceWidth * scale);
                const resizedHeight = Math.round(sourceHeight * scale);
                const padX = Math.floor((MODEL_SIZE - resizedWidth) / 2);
                const padY = Math.floor((MODEL_SIZE - resizedHeight) / 2);
                const canvas = inputCanvasRef.current;
                const context = canvas.getContext("2d", {
                    willReadFrequently: true,
                });

                canvas.width = MODEL_SIZE;
                canvas.height = MODEL_SIZE;

                context.fillStyle = "rgb(114, 114, 114)";
                context.fillRect(0, 0, MODEL_SIZE, MODEL_SIZE);
                context.drawImage(
                    video,
                    padX,
                    padY,
                    resizedWidth,
                    resizedHeight,
                );
                const canvasFinishedAt = performance.now();

                const imageData = context.getImageData(
                    0,
                    0,
                    MODEL_SIZE,
                    MODEL_SIZE,
                );

                const pixelsFinishedAt = performance.now();
                let tensor;

                if (MODELS[modelType].channels === 1) {
                    const grayscale = new Float32Array(MODEL_SIZE * MODEL_SIZE);

                    for (let pixel = 0; pixel < grayscale.length; pixel++) {
                        const offset = pixel * 4;

                        grayscale[pixel] =
                            (imageData.data[offset] * 0.299 +
                                imageData.data[offset + 1] * 0.587 +
                                imageData.data[offset + 2] * 0.114) /
                            255;
                    }

                    tensor = new ort.Tensor("float32", grayscale, [
                        1,
                        1,
                        MODEL_SIZE,
                        MODEL_SIZE,
                    ]);
                } else {
                    tensor = await ort.Tensor.fromImage(imageData, {
                        dataType: "float32",
                        tensorFormat: "RGB",
                        tensorLayout: "NCHW",
                    });
                }
                const tensorFinishedAt = performance.now();

                const results = await sessionRef.current.run({
                    [sessionRef.current.inputNames[0]]: tensor,
                });

                const inferenceFinishedAt = performance.now();

                tensor.dispose?.();

                if (cancelled) return;

                const output = results[sessionRef.current.outputNames[0]];
                const columns = output.dims[output.dims.length - 1];

                if (columns < 6) {
                    throw new Error(
                        `Salida ONNX inesperada: [${output.dims.join(", ")}]. Exporta con nms=True.`,
                    );
                }

                const detections = [];

                for (
                    let offset = 0;
                    offset < output.data.length;
                    offset += columns
                ) {
                    const score = output.data[offset + 4];
                    const classId = Math.round(output.data[offset + 5]);

                    if (score < confidence || !CLASS_NAMES[classId]) {
                        continue;
                    }

                    const x1 = Math.max(
                        0,
                        Math.min(
                            sourceWidth,
                            (output.data[offset] - padX) / scale,
                        ),
                    );
                    const y1 = Math.max(
                        0,
                        Math.min(
                            sourceHeight,
                            (output.data[offset + 1] - padY) / scale,
                        ),
                    );
                    const x2 = Math.max(
                        0,
                        Math.min(
                            sourceWidth,
                            (output.data[offset + 2] - padX) / scale,
                        ),
                    );
                    const y2 = Math.max(
                        0,
                        Math.min(
                            sourceHeight,
                            (output.data[offset + 3] - padY) / scale,
                        ),
                    );

                    if (x2 > x1 && y2 > y1) {
                        detections.push({
                            label: CLASS_NAMES[classId],
                            confidence: score,
                            box: [x1, y1, x2, y2],
                        });
                    }
                }

                detections.sort(
                    (first, second) => second.confidence - first.confidence,
                );

                const postprocessFinishedAt = performance.now();

                setAspectRatio(`${sourceWidth} / ${sourceHeight}`);
                setError("");

                const overlay = overlayCanvasRef.current;

                overlay.width = sourceWidth;
                overlay.height = sourceHeight;

                const overlayContext = overlay.getContext("2d");

                overlayContext.clearRect(0, 0, sourceWidth, sourceHeight);
                overlayContext.font = "600 18px Arial";
                overlayContext.lineWidth = 4;

                detections.forEach(({ label, confidence: score, box }) => {
                    const [x1, y1, x2, y2] = box;
                    const text = `${label} ${Math.round(score * 100)}%`;
                    const textWidth =
                        overlayContext.measureText(text).width + 16;

                    overlayContext.strokeStyle = "#7e57c2";
                    overlayContext.fillStyle = "#7e57c2";

                    overlayContext.strokeRect(x1, y1, x2 - x1, y2 - y1);
                    overlayContext.fillRect(
                        x1,
                        Math.max(0, y1 - 30),
                        textWidth,
                        30,
                    );

                    overlayContext.fillStyle = "#fff";

                    overlayContext.fillText(text, x1 + 8, Math.max(21, y1 - 8));
                });

                const best = detections[0];

                if (best) {
                    stableDetectionRef.current =
                        best.label === stableDetectionRef.current.label
                            ? {
                                  label: best.label,
                                  hits: stableDetectionRef.current.hits + 1,
                                  misses: 0,
                              }
                            : {
                                  label: best.label,
                                  hits: 1,
                                  misses: 0,
                              };

                    if (stableDetectionRef.current.hits >= 3) {
                        setDetectedCard(best);
                    }
                } else {
                    stableDetectionRef.current.hits = 0;
                    stableDetectionRef.current.misses += 1;
                }

                const finishedAt = performance.now();

                setInferenceMs({
                    total: finishedAt - totalStartedAt,
                    canvas: canvasFinishedAt - totalStartedAt,
                    pixels: pixelsFinishedAt - canvasFinishedAt,
                    tensor: tensorFinishedAt - pixelsFinishedAt,
                    model: inferenceFinishedAt - tensorFinishedAt,
                    results: postprocessFinishedAt - inferenceFinishedAt,
                    overlay: finishedAt - postprocessFinishedAt,
                });
            } catch (inferenceError) {
                if (!cancelled) {
                    cancelled = true;
                    void reportError(
                        "card-detector-inference",
                        inferenceError,
                        {
                            modelType,
                            modelStatus,
                            provider,
                            video: {
                                width: videoRef.current?.videoWidth || null,
                                height: videoRef.current?.videoHeight || null,
                                readyState:
                                    videoRef.current?.readyState || null,
                            },
                            inputNames: sessionRef.current?.inputNames || [],
                            outputNames: sessionRef.current?.outputNames || [],
                        },
                    );
                    stopCamera();

                    setError(
                        `Error durante la detección: ${inferenceError.message}`,
                    );
                }
            } finally {
                inferenceRunningRef.current = false;

                if (!cancelled) {
                    timeout = window.setTimeout(detect, 0);
                }
            }
        };

        detect();

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [
        cameraActive,
        confidence,
        modelStatus,
        modelType,
        stopCamera,
        provider,
    ]);

    const card = useMemo(() => {
        if (!detectedCard) return null;

        const suit = SUITS[detectedCard.label.slice(-1)];
        const rank = detectedCard.label.slice(0, -1);

        return {
            ...detectedCard,
            suit,
            rank,
            name: `${RANKS[rank] || rank} de ${suit.name}`,
            reloj: `${SHORT_RANKS[rank]}:${SHORT_SUITS[suit.symbol]}`,
        };
    }, [detectedCard]);

    const cardPictogram = useMemo(() => {
        if (!card || !selectedPreset) return "";

        const code = Object.entries(selectedPreset.cardsByCode || {}).find(
            ([, value]) =>
                getCardKeyFromText(value) === `${card.rank}${card.suit.symbol}`,
        )?.[0];
        const letter = code?.[dealIndex];

        return letter ? presetPictograms[letter] || letter : "";
    }, [card, dealIndex, presetPictograms, selectedPreset]);

    const [useAudio, setUseAudio] = useState(true);

    const [lockMode, setLockMode] = useState(false);
    const [lockDate, setLockDate] = useState(() => new Date());

    useEffect(() => {
        if (!lockMode) return undefined;

        setLockDate(new Date());

        const interval = window.setInterval(
            () => setLockDate(new Date()),
            1000,
        );

        return () => window.clearInterval(interval);
    }, [lockMode]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) setLockMode(false);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () =>
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreenChange,
            );
    }, []);

    useEffect(() => {
        if (!useAudio || lockMode) {
            window.speechSynthesis?.cancel();
            return undefined;
        }

        const text = PICTOGRAM_SPEECH[cardPictogram.replace(/\uFE0F/g, "")];

        if (!text || !("speechSynthesis" in window)) return undefined;

        window.speechSynthesis.cancel();

        const speech = new window.SpeechSynthesisUtterance(text);

        speech.lang = "es-ES";
        speech.rate = 1.15;

        window.speechSynthesis.speak(speech);

        return () => window.speechSynthesis.cancel();
    }, [cardPictogram, detectedCard?.label, lockMode, useAudio]);

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Detector de cartas
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        La detección se ejecuta en este dispositivo. Muestra la
                        carta de frente y deja sus índices visibles.
                    </Typography>
                </Box>

                <Divider />
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ sm: "center" }}
                    >
                        <FormControl
                            size="small"
                            disabled={!radicalPresets.length}
                            sx={{ minWidth: { xs: "100%", sm: 280 } }}
                        >
                            <InputLabel id="radical-preset-label">
                                Preset Radical
                            </InputLabel>

                            <Select
                                labelId="radical-preset-label"
                                label="Preset Radical"
                                value={selectedPresetId}
                                onChange={(event) => {
                                    setSelectedPresetId(event.target.value);
                                    setDealIndex(0);
                                }}
                            >
                                <MenuItem value="" disabled>
                                    {radicalPresets.length
                                        ? "Selecciona un preset"
                                        : "No hay presets guardados"}
                                </MenuItem>

                                {radicalPresets.map((preset) => (
                                    <MenuItem key={preset.id} value={preset.id}>
                                        {preset.name} · {preset.deals}{" "}
                                        {preset.deals === 1 ? "dada" : "dadas"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedPreset && (
                            <>
                                <Chip
                                    label={`Dada ${dealIndex + 1} de ${selectedPreset.deals}`}
                                    color="primary"
                                    variant="outlined"
                                />

                                {selectedPreset.deals > 1 && (
                                    <Button
                                        variant="contained"
                                        startIcon={<SkipNextIcon />}
                                        onClick={() =>
                                            setDealIndex(
                                                (previous) =>
                                                    (previous + 1) %
                                                    selectedPreset.deals,
                                            )
                                        }
                                    >
                                        Siguiente dada
                                    </Button>
                                )}
                            </>
                        )}
                        {useAudio ? (
                            <IconButton
                                variant="outlined"
                                onClick={() => setUseAudio(false)}
                            >
                                <VolumeOffIcon />
                            </IconButton>
                        ) : (
                            <IconButton
                                variant="outlined"
                                onClick={() => setUseAudio(true)}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<FullscreenIcon />}
                            disabled={!cameraActive}
                            onClick={async () => {
                                setUseAudio(false);
                                window.speechSynthesis?.cancel();
                                setLockDate(new Date());
                                setLockMode(true);

                                try {
                                    await document.documentElement.requestFullscreen?.();
                                } catch (fullscreenError) {
                                    console.warn(
                                        "Fullscreen mode could not be enabled.",
                                        fullscreenError,
                                    );
                                }
                            }}
                        >
                            Modo bloqueo
                        </Button>
                    </Stack>
                </Paper>

                {error && <Alert severity="error">{error}</Alert>}

                <Stack
                    direction={{
                        xs: "column",
                        sm: "row",
                    }}
                    spacing={1.5}
                    alignItems={{ sm: "center" }}
                >
                    <FormControl
                        size="small"
                        disabled={cameraActive || loadingCamera}
                        sx={{ minWidth: 190 }}
                    >
                        <InputLabel id="card-model-label">Modelo</InputLabel>

                        <Select
                            labelId="card-model-label"
                            label="Modelo"
                            value={modelType}
                            onChange={(event) => {
                                setModelType(event.target.value);
                                setDetectedCard(null);
                                setInferenceMs(null);
                            }}
                        >
                            {Object.entries(MODELS).map(([value, model]) => (
                                <MenuItem key={value} value={value}>
                                    {model.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={
                            loadingCamera ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <PlayArrowIcon />
                            )
                        }
                        disabled={
                            modelStatus !== "ready" ||
                            cameraActive ||
                            loadingCamera
                        }
                        onClick={() => startCamera()}
                    >
                        Iniciar cámara
                    </Button>

                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<StopIcon />}
                        disabled={!cameraActive}
                        onClick={stopCamera}
                    >
                        Detener
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<CameraswitchIcon />}
                        disabled={!cameraActive || loadingCamera}
                        onClick={async () => {
                            const mode =
                                facingMode === "environment"
                                    ? "user"
                                    : "environment";

                            setFacingMode(mode);
                            await startCamera(mode);
                        }}
                    >
                        Cambiar cámara
                    </Button>

                    <Chip
                        size="small"
                        color={
                            modelStatus === "ready"
                                ? "success"
                                : modelStatus === "error"
                                  ? "error"
                                  : "default"
                        }
                        icon={
                            modelStatus === "loading" ? (
                                <CircularProgress size={14} />
                            ) : undefined
                        }
                        label={
                            modelStatus === "loading"
                                ? "Cargando modelo"
                                : modelStatus === "error"
                                  ? "Modelo no disponible"
                                  : `Modelo · ${provider}`
                        }
                    />

                    {inferenceMs !== null && (
                        <Stack spacing={0.25} alignItems="flex-start">
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Total · ${inferenceMs.total.toFixed(1)} ms`}
                            />

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {`Canvas ${inferenceMs.canvas.toFixed(1)} ms · Píxeles ${inferenceMs.pixels.toFixed(1)} ms · Tensor ${inferenceMs.tensor.toFixed(1)} ms`}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {`Modelo ${inferenceMs.model.toFixed(1)} ms · Resultados ${inferenceMs.results.toFixed(1)} ms · Overlay ${inferenceMs.overlay.toFixed(1)} ms`}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                <Stack
                    direction={{
                        xs: "column",
                        lg: "row",
                    }}
                    spacing={2}
                    alignItems="stretch"
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 1,
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                aspectRatio,
                                bgcolor: "#111",
                                borderRadius: 1,
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                component="video"
                                ref={videoRef}
                                muted
                                playsInline
                                autoPlay
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "block",
                                    objectFit: "fill",
                                }}
                            />

                            <Box
                                component="canvas"
                                ref={overlayCanvasRef}
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    pointerEvents: "none",
                                }}
                            />

                            {!cameraActive && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    spacing={1}
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        color: "grey.400",
                                    }}
                                >
                                    {modelStatus === "loading" ? (
                                        <CircularProgress color="inherit" />
                                    ) : (
                                        <PlayArrowIcon
                                            sx={{
                                                fontSize: 48,
                                            }}
                                        />
                                    )}

                                    <Typography variant="body2">
                                        {modelStatus === "loading"
                                            ? "Cargando el modelo ONNX…"
                                            : "Inicia la cámara para comenzar"}
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        <canvas ref={inputCanvasRef} hidden />
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            width: {
                                xs: "100%",
                                lg: 280,
                            },
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 280,
                        }}
                    >
                        {card ? (
                            <Stack spacing={1.5} alignItems="center">
                                <Paper
                                    elevation={3}
                                    sx={{
                                        width: 130,
                                        aspectRatio: "5 / 7",
                                        p: 1.5,
                                        color: card.suit.color,
                                        bgcolor: "#fff",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 0.9,
                                        }}
                                    >
                                        {card.rank}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 62,
                                            lineHeight: 1,
                                            textAlign: "center",
                                        }}
                                    >
                                        {card.suit.symbol}
                                    </Typography>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 0.9,
                                            transform: "rotate(180deg)",
                                        }}
                                    >
                                        {card.rank}
                                    </Typography>
                                </Paper>

                                <Box
                                    sx={{
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        {card.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Confianza:{" "}
                                        {Math.round(card.confidence * 100)}%
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : (
                            <Box
                                sx={{
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                >
                                    Sin carta estable
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    La carta aparecerá al detectarse en tres
                                    imágenes consecutivas.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Stack>

                <Paper variant="outlined" sx={{ px: 2, py: 1.25 }}>
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                minWidth: 170,
                                fontWeight: 700,
                            }}
                        >
                            Confianza mínima: {Math.round(confidence * 100)}%
                        </Typography>

                        <Slider
                            size="small"
                            min={0.25}
                            max={0.9}
                            step={0.05}
                            value={confidence}
                            onChange={(_, value) => setConfidence(value)}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) =>
                                `${Math.round(value * 100)}%`
                            }
                        />
                    </Stack>
                </Paper>
            </Stack>
            {!lockMode && card && selectedPreset && (
                <Paper
                    elevation={12}
                    sx={{
                        position: "fixed",
                        right: { xs: 12, sm: 24 },
                        bottom: {
                            xs: "calc(12px + env(safe-area-inset-bottom))",
                            sm: 24,
                        },
                        zIndex: (theme) => theme.zIndex.snackbar,
                        minWidth: 130,
                        px: 2,
                        py: 1.5,
                        border: 2,
                        borderColor: cardPictogram
                            ? "primary.main"
                            : "warning.main",
                        borderRadius: 3,
                        textAlign: "center",
                        pointerEvents: "none",
                    }}
                >
                    <Typography sx={{ fontSize: 72, lineHeight: 1 }}>
                        {cardPictogram || "?"}
                    </Typography>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.75 }}
                    >
                        {cardPictogram
                            ? `${card.rank}${card.suit.symbol} · Dada ${dealIndex + 1}`
                            : `${card.rank}${card.suit.symbol} no está en el preset`}
                    </Typography>
                </Paper>
            )}
            {lockMode && (
                <Box
                    title="Doble toque para salir"
                    onDoubleClick={async () => {
                        setLockMode(false);

                        try {
                            await document.exitFullscreen?.();
                        } catch (fullscreenError) {
                            console.warn(
                                "Fullscreen mode could not be closed.",
                                fullscreenError,
                            );
                        }
                    }}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        width: "100vw",
                        height: "100dvh",
                        overflow: "hidden",
                        bgcolor: LOCK_SCREEN_STYLE.backgroundColor,
                        color: LOCK_SCREEN_STYLE.textColor,
                        zIndex: LOCK_SCREEN_STYLE.zIndex,
                        userSelect: "none",
                        touchAction: "manipulation",
                    }}
                >
                    <Stack
                        alignItems="center"
                        sx={{
                            position: "absolute",
                            top: LOCK_SCREEN_STYLE.contentTop,
                            left: 0,
                            right: 0,
                            px: LOCK_SCREEN_STYLE.horizontalPadding,
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{
                                color: "inherit",
                                fontFamily: LOCK_SCREEN_STYLE.fontFamily,
                                fontSize: LOCK_SCREEN_STYLE.timeFontSize,
                                fontWeight: LOCK_SCREEN_STYLE.timeFontWeight,
                                letterSpacing:
                                    LOCK_SCREEN_STYLE.timeLetterSpacing,
                                lineHeight: LOCK_SCREEN_STYLE.timeLineHeight,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {LOCK_TIME_FORMAT.format(lockDate)}
                        </Typography>

                        <Typography
                            component="div"
                            sx={{
                                color: "inherit",
                                fontFamily: LOCK_SCREEN_STYLE.fontFamily,
                                fontSize: LOCK_SCREEN_STYLE.dateFontSize,
                                fontWeight: LOCK_SCREEN_STYLE.dateFontWeight,
                                lineHeight: 1.2,
                            }}
                        >
                            {LOCK_DATE_FORMAT.format(lockDate)}
                        </Typography>

                        <Stack
                            spacing={LOCK_SCREEN_STYLE.alarmGap}
                            alignItems="center"
                            sx={{ mt: LOCK_SCREEN_STYLE.alarmMarginTop }}
                        >
                            <AccessAlarmIcon
                                sx={{
                                    fontSize: LOCK_SCREEN_STYLE.alarmIconSize,
                                }}
                            />

                            <Typography
                                sx={{
                                    color: "inherit",
                                    fontFamily: LOCK_SCREEN_STYLE.fontFamily,
                                    fontSize: LOCK_SCREEN_STYLE.cardFontSize,
                                    fontWeight:
                                        LOCK_SCREEN_STYLE.cardFontWeight,
                                    lineHeight: 1.2,
                                    textAlign: "center",
                                }}
                            >
                                {card?.reloj || "Desactiva..."}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default CardDetectorScreen;
